import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Card, CardContent, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, Tooltip, Skeleton, Alert, Button, Stack, Divider,
} from '@mui/material';
import {
  TrendingUp, TrendingDown, PersonAdd, ShoppingCart,
  Phone as PhoneIcon, Refresh as RefreshIcon,
  AttachMoney as MoneyIcon, CheckCircle, RemoveShoppingCart,
  WhatsApp as WhatsAppIcon, LocalFireDepartment as FireIcon,
  AccessTime as ClockIcon, ArrowForward as ArrowIcon, TrendingFlat,
} from '@mui/icons-material';
import { apiClient } from '../services/api';
import PeriodSelector from '../components/common/PeriodSelector';
import { computePresetRange, DEFAULT_DATE_RANGE_PRESET } from '../utils/dateRangePresets';

// ── types ──────────────────────────────────────────────────────────────────────

interface FunnelStage {
  stage: string; count: number; color: string;
  pctOfTop: number; conversionFromPrev: number; dropOffRate: number;
}

interface ProcurementItem {
  productId: string; name: string; sku: string | null; category: string | null;
  views: number; cart_adds: number; conversionRate: number;
  current_stock: number; urgency: 'Critical' | 'High' | 'Moderate' | 'Healthy';
  suggested_reorder_qty: number;
}

interface DormantVIP {
  id: string; name: string; email: string; mobileNo: string | null;
  customerType: string; lifetimeRevenue: number; lastOrderDate: string | null;
  daysSinceLastOrder: number; orderCount: number;
}

interface AbandonedCartsData {
  addToCartSessions: number; orderedSessions: number;
  abandonedSessions: number; abandonmentRate: number;
  checkoutSessions: number; checkoutAbandonedSessions: number;
  checkoutAbandonmentRate: number;
  topAbandonedProducts: Array<{ productId: string; name: string; addCount: number }>;
  total_stranded_value: number; total_active_carts: number;
  hot_carts_count: number; warm_carts_count: number; stale_carts_count: number;
}

interface Metrics {
  totalCustomers: number; newCustomers: number; activeCustomers: number;
  averageOrderValue: number; retentionRate: number; growthRate: number;
  totalPageViews: number; totalProductViews: number; totalAddToCart: number;
  totalCheckouts: number; totalOrdersPlaced: number;
  cartToCheckoutRate: number; checkoutToOrderRate: number;
  guestSessions: number; guestSessionsGrowthRate: number;
}

interface TopCustomer {
  id: string; name: string; email: string; mobileNo: string | null;
  customerType: string; totalOrders: number; totalRevenue: number;
  lastOrderDate: string | null; averageOrderValue: number;
}

interface DecisionMatrix {
  metrics: Metrics;
  funnel: FunnelStage[];
  procurement_radar: ProcurementItem[];
  search_trends: { top_queries: any[]; zero_result_queries: any[] };
  abandoned_carts: AbandonedCartsData;
  dormant_vips: DormantVIP[];
}

// ── helpers ────────────────────────────────────────────────────────────────────

const fmtINR = (n: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
const fmt = (n: number) => new Intl.NumberFormat('en-IN').format(n);

const URGENCY_COLOR: Record<string, string> = {
  Critical: '#c62828', High: '#f57c00', Moderate: '#1976d2', Healthy: '#2e7d32',
};

// ── sub-components ─────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, delta, icon, accent }: {
  label: string; value: string; sub?: string; delta?: number;
  icon: React.ReactNode; accent: string;
}) {
  const DeltaIcon = delta === undefined ? TrendingFlat : delta >= 0 ? TrendingUp : TrendingDown;
  const deltaColor = delta === undefined ? 'text.disabled' : delta >= 0 ? '#2e7d32' : '#c62828';
  return (
    <Card sx={{ height: '100%', borderTop: `3px solid ${accent}`, position: 'relative', overflow: 'hidden' }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Box sx={{ position: 'absolute', right: 10, top: 10, color: accent, opacity: 0.1, fontSize: 52, lineHeight: 1 }}>
          {icon}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.62rem' }}>
          {label}
        </Typography>
        <Typography variant="h4" fontWeight={800} mt={0.5} color="text.primary" sx={{ lineHeight: 1.1 }}>
          {value}
        </Typography>
        <Box display="flex" alignItems="center" gap={0.5} mt={0.75}>
          {delta !== undefined && <DeltaIcon sx={{ fontSize: 13, color: deltaColor }} />}
          <Typography variant="caption" sx={{ color: delta !== undefined ? deltaColor : 'text.disabled', fontWeight: 600 }}>
            {delta !== undefined ? `${Math.abs(delta).toFixed(1)}% vs prev` : sub}
          </Typography>
        </Box>
        {delta !== undefined && sub && (
          <Typography variant="caption" color="text.secondary" display="block">{sub}</Typography>
        )}
      </CardContent>
    </Card>
  );
}

function SectionHeader({ title, subtitle, action }: { title: string; subtitle?: string; action?: React.ReactNode }) {
  return (
    <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1.5}>
      <Box>
        <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
        {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
      </Box>
      {action}
    </Box>
  );
}

// ── main component ─────────────────────────────────────────────────────────────

const CustomerAnalyticsDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState(() => computePresetRange(DEFAULT_DATE_RANGE_PRESET));
  const [matrix, setMatrix] = useState<DecisionMatrix | null>(null);
  const [topCustomers, setTopCustomers] = useState<TopCustomer[]>([]);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (matrix) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const params = { period: 'custom', date_from: dateRange.dateFrom, date_to: dateRange.dateTo };
      const [matrixRes, custRes] = await Promise.all([
        apiClient.get('/admin/analytics/customers/decision-matrix', { params }),
        apiClient.get('/admin/analytics/customers/top-customers', { params: { limit: 8 } }),
      ]);
      setMatrix(matrixRes.data);
      setTopCustomers(custRes.data);
    } catch (err: any) {
      const status = err?.response?.status;
      setError(status === 403
        ? "No permission to view customer analytics."
        : 'Backend unreachable — please start the server and retry.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <Box>
      <Skeleton variant="rectangular" height={32} width={280} sx={{ mb: 3, borderRadius: 1 }} />
      <Grid container spacing={2} mb={3}>
        {[0,1,2,3,4].map(i => <Grid item xs={6} sm={4} md key={i}><Skeleton variant="rounded" height={110} /></Grid>)}
      </Grid>
      <Skeleton variant="rounded" height={130} sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}><Skeleton variant="rounded" height={280} /></Grid>
        <Grid item xs={12} md={5}><Skeleton variant="rounded" height={280} /></Grid>
      </Grid>
    </Box>
  );

  if (!matrix) return (
    <Alert
      severity={error?.includes('permission') ? 'warning' : 'error'}
      action={<Button color="inherit" size="small" onClick={fetchData}>Retry</Button>}
    >
      {error || 'Failed to load analytics.'}
    </Alert>
  );

  const { metrics, funnel, procurement_radar, abandoned_carts, dormant_vips } = matrix;
  const periodRevenue = metrics.activeCustomers * metrics.averageOrderValue;
  const criticalItems = procurement_radar.filter(p => p.urgency === 'Critical' || p.urgency === 'High');

  return (
    <Box sx={{ opacity: refreshing ? 0.65 : 1, transition: 'opacity 0.15s' }}>

      {/* ── Header ─────────────────────────────────────────────────────────────── */}
      <Box display="flex" gap={2} alignItems="center" mb={3} flexWrap="wrap">
        <Box flex={1}>
          <Typography variant="h5" fontWeight={800}>Customer Analytics</Typography>
          <Typography variant="body2" color="text.secondary">Executive view · one glance, clear decision</Typography>
        </Box>
        <PeriodSelector onChange={(df, dt) => setDateRange({ dateFrom: df, dateTo: dt })} />
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={fetchData} color="primary" disabled={refreshing} size="small">
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* ── KPI Scorecard ─────────────────────────────────────────────────────── */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={4} md>
          <KpiCard label="Period Revenue" value={fmtINR(periodRevenue)}
            delta={metrics.growthRate} sub={`${metrics.activeCustomers} paying customers`}
            icon={<MoneyIcon fontSize="inherit" />} accent="#1565c0" />
        </Grid>
        <Grid item xs={6} sm={4} md>
          <KpiCard
            label="Retention Rate"
            value={`${metrics.retentionRate.toFixed(1)}%`}
            sub={`${metrics.activeCustomers} of ${metrics.totalCustomers} ordered`}
            icon={<CheckCircle fontSize="inherit" />}
            accent={metrics.retentionRate >= 40 ? '#2e7d32' : metrics.retentionRate >= 20 ? '#f57c00' : '#c62828'}
          />
        </Grid>
        <Grid item xs={6} sm={4} md>
          <KpiCard label="Avg Order Value" value={fmtINR(metrics.averageOrderValue)}
            icon={<ShoppingCart fontSize="inherit" />} accent="#0277bd" />
        </Grid>
        <Grid item xs={6} sm={4} md>
          <KpiCard
            label="Cart Abandonment"
            value={`${abandoned_carts.abandonmentRate}%`}
            sub={`${fmt(abandoned_carts.abandonedSessions)} carts lost`}
            icon={<RemoveShoppingCart fontSize="inherit" />}
            accent={abandoned_carts.abandonmentRate > 60 ? '#c62828' : abandoned_carts.abandonmentRate > 40 ? '#f57c00' : '#2e7d32'}
          />
        </Grid>
        <Grid item xs={6} sm={4} md>
          <KpiCard label="New Customers" value={fmt(metrics.newCustomers)}
            delta={metrics.growthRate} sub={`${fmt(metrics.guestSessions)} guest sessions`}
            icon={<PersonAdd fontSize="inherit" />} accent="#8e24aa" />
        </Grid>
      </Grid>

      {/* ── Conversion Funnel ─────────────────────────────────────────────────── */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)' }}>
        <CardContent sx={{ py: 2 }}>
          <Typography variant="subtitle2" color="white" fontWeight={700} mb={2}>
            Conversion Funnel — where customers drop off
          </Typography>
          <Box display="flex" alignItems="stretch" gap={0.5} sx={{ overflowX: 'auto' }}>
            {funnel.map((stage, i) => {
              const isLast = i === funnel.length - 1;
              return (
                <Box key={stage.stage} display="flex" alignItems="center" flex={1} minWidth={90}>
                  <Box flex={1}>
                    <Box sx={{
                      bgcolor: stage.color, borderRadius: 1, p: 1.5,
                      opacity: 0.75 + 0.25 * (1 - i / funnel.length),
                    }}>
                      <Typography variant="h6" color="white" fontWeight={800} lineHeight={1}>
                        {fmt(stage.count)}
                      </Typography>
                      <Typography variant="caption" color="rgba(255,255,255,0.8)" sx={{ fontSize: '0.58rem', display: 'block', mt: 0.3 }}>
                        {stage.stage}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.62rem', fontWeight: 700 }}>
                        {stage.pctOfTop.toFixed(0)}% of top
                      </Typography>
                    </Box>
                    {i > 0 && (
                      <Box textAlign="center" mt={0.5}>
                        <Chip label={`${stage.conversionFromPrev}%`} size="small" sx={{
                          fontSize: '0.58rem', height: 16,
                          bgcolor: stage.conversionFromPrev >= 50 ? 'rgba(46,125,50,0.25)' : stage.conversionFromPrev >= 20 ? 'rgba(245,124,0,0.25)' : 'rgba(198,40,40,0.25)',
                          color: stage.conversionFromPrev >= 50 ? '#2e7d32' : stage.conversionFromPrev >= 20 ? '#f57c00' : '#c62828',
                        }} />
                      </Box>
                    )}
                  </Box>
                  {!isLast && (
                    <Box sx={{ px: 0.5, color: 'rgba(255,255,255,0.25)', flexShrink: 0 }}>
                      <ArrowIcon sx={{ fontSize: 14 }} />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </CardContent>
      </Card>

      {/* ── Dormant VIPs + Stranded Revenue ──────────────────────────────────── */}
      <Grid container spacing={2} mb={3}>

        {/* Dormant VIPs */}
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <SectionHeader
                title="⚠️ Dormant VIPs — Re-engage Now"
                subtitle={`${dormant_vips.length} high-value customers inactive 30+ days`}
                action={dormant_vips.length > 0
                  ? <Chip label="Action Required" color="error" size="small" variant="outlined" />
                  : undefined}
              />
              {dormant_vips.length === 0 ? (
                <Box textAlign="center" py={3}>
                  <CheckCircle sx={{ color: 'success.main', fontSize: 40 }} />
                  <Typography color="text.secondary" mt={1} variant="body2">All VIPs are active — excellent retention!</Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['Customer', 'Lifetime Rev', 'Silent for', 'Contact'].map(h => (
                          <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.68rem', color: 'text.secondary', py: 0.75 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dormant_vips.map(vip => (
                        <TableRow key={vip.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{vip.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{vip.orderCount} orders</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700} color="primary.main">{fmtINR(vip.lifetimeRevenue)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={`${vip.daysSinceLastOrder}d`} size="small" sx={{
                              fontWeight: 700, fontSize: '0.68rem',
                              bgcolor: vip.daysSinceLastOrder > 90 ? '#c6282820' : vip.daysSinceLastOrder > 60 ? '#f57c0020' : 'transparent',
                              color: vip.daysSinceLastOrder > 90 ? '#c62828' : vip.daysSinceLastOrder > 60 ? '#f57c00' : 'text.primary',
                            }} />
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={0.5}>
                              <Tooltip title={vip.mobileNo ? 'Call' : 'No number'}>
                                <span>
                                  <IconButton size="small" disabled={!vip.mobileNo} color="primary"
                                    onClick={() => vip.mobileNo && (window.location.href = `tel:${vip.mobileNo}`)}>
                                    <PhoneIcon sx={{ fontSize: 13 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={vip.mobileNo ? 'WhatsApp' : 'No number'}>
                                <span>
                                  <IconButton size="small" disabled={!vip.mobileNo}
                                    sx={{ color: vip.mobileNo ? '#25D366' : undefined }}
                                    onClick={() => vip.mobileNo && window.open(`https://wa.me/91${vip.mobileNo.replace(/\D/g, '')}`, '_blank')}>
                                    <WhatsAppIcon sx={{ fontSize: 13 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Stranded Revenue */}
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <SectionHeader title="💰 Stranded Revenue" subtitle="Value sitting in active carts" />
              <Box sx={{
                p: 2, borderRadius: 2, mb: 2, textAlign: 'center',
                background: 'linear-gradient(135deg, #6a1b9a15, #1565c015)',
                border: '1px solid #1565c030',
              }}>
                <Typography variant="h3" fontWeight={800} color="primary.main">
                  {fmtINR(abandoned_carts.total_stranded_value)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  across {fmt(abandoned_carts.total_active_carts)} active carts
                </Typography>
              </Box>

              <Stack spacing={1.5}>
                {[
                  { icon: <FireIcon sx={{ color: '#c62828', fontSize: 18 }} />, label: 'Hot — last 24h', sub: 'high intent, act fast', count: abandoned_carts.hot_carts_count, color: 'error' as const },
                  { icon: <ClockIcon sx={{ color: '#f57c00', fontSize: 18 }} />, label: 'Warm — 1–3 days', sub: 'send reminder', count: abandoned_carts.warm_carts_count, color: 'warning' as const },
                  { icon: <RemoveShoppingCart sx={{ color: '#94A3B8', fontSize: 18 }} />, label: 'Stale — 3+ days', sub: 'discount or drop', count: abandoned_carts.stale_carts_count, color: 'default' as const },
                ].map(row => (
                  <Box key={row.label} display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={1}>
                      {row.icon}
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{row.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{row.sub}</Typography>
                      </Box>
                    </Box>
                    <Chip label={fmt(row.count)} size="small" color={row.color} />
                  </Box>
                ))}
              </Stack>

              {abandoned_carts.topAbandonedProducts.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Top Products Abandoned
                  </Typography>
                  {abandoned_carts.topAbandonedProducts.slice(0, 3).map((p, i) => (
                    <Box key={p.productId} display="flex" alignItems="center" gap={1} mt={0.75}>
                      <Typography variant="caption" sx={{ width: 14, color: 'text.disabled', fontWeight: 700 }}>{i + 1}</Typography>
                      <Typography variant="body2" flex={1} noWrap>{p.name}</Typography>
                      <Typography variant="caption" color="warning.main" fontWeight={700}>{p.addCount}×</Typography>
                    </Box>
                  ))}
                  <Button fullWidth size="small" variant="outlined" startIcon={<WhatsAppIcon />}
                    sx={{ mt: 2, color: '#25D366', borderColor: '#25D366', '&:hover': { borderColor: '#128C7E', color: '#128C7E' } }}
                    onClick={() => window.open('https://wa.me', '_blank')}>
                    WhatsApp Recovery Campaign
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Procurement Radar ─────────────────────────────────────────────────── */}
      {criticalItems.length > 0 && (
        <Card sx={{ mb: 3, border: '1px solid #f57c0040' }}>
          <CardContent>
            <SectionHeader
              title="📦 Procurement Radar — Restock Urgently"
              subtitle={`${criticalItems.length} products have high demand vs low stock`}
              action={<Chip label={`${criticalItems.filter(p => p.urgency === 'Critical').length} Critical`} color="error" size="small" />}
            />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#1a237e' }}>
                    {['Product', 'Category', 'Demand', 'Cart Adds', 'Stock', 'Urgency', 'Reorder Qty'].map(h => (
                      <TableCell key={h} sx={{ color: 'white', fontWeight: 700, fontSize: '0.68rem', py: 1 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {criticalItems.slice(0, 10).map(item => (
                    <TableRow key={item.productId} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{item.name}</Typography>
                        {item.sku && <Typography variant="caption" color="text.secondary">{item.sku}</Typography>}
                      </TableCell>
                      <TableCell><Typography variant="caption">{item.category || '—'}</Typography></TableCell>
                      <TableCell><Typography variant="body2" fontWeight={600}>{fmt(item.views)}</Typography></TableCell>
                      <TableCell><Typography variant="body2">{fmt(item.cart_adds)}</Typography></TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}
                          color={item.current_stock <= 3 ? 'error.main' : item.current_stock <= 15 ? 'warning.main' : 'text.primary'}>
                          {item.current_stock}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={item.urgency} size="small" sx={{
                          bgcolor: `${URGENCY_COLOR[item.urgency]}20`,
                          color: URGENCY_COLOR[item.urgency],
                          fontWeight: 700, fontSize: '0.65rem',
                        }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700} color="primary.main">
                          {fmt(item.suggested_reorder_qty)} units
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* ── Top Customers + Trending Products ─────────────────────────────────── */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <SectionHeader title="🏆 Top Customers by Revenue" subtitle="All-time leaderboard" />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['#', 'Customer', 'Revenue', 'Orders', ''].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.68rem', color: 'text.secondary', py: 0.75 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topCustomers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography variant="body2" color="text.secondary" py={2}>No orders yet</Typography>
                        </TableCell>
                      </TableRow>
                    ) : topCustomers.map((c, i) => (
                      <TableRow key={c.id} hover>
                        <TableCell>
                          <Typography variant="caption" fontWeight={700}
                            color={i === 0 ? 'warning.main' : i === 1 ? '#94A3B8' : 'text.disabled'}>
                            {i + 1}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} noWrap>{c.name}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>{c.email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700} color="primary.main">{fmtINR(c.totalRevenue)}</Typography>
                          <Typography variant="caption" color="text.secondary">AOV {fmtINR(c.averageOrderValue)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{c.totalOrders}</Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={c.mobileNo ? 'WhatsApp' : 'No phone'}>
                            <span>
                              <IconButton size="small" disabled={!c.mobileNo}
                                sx={{ color: c.mobileNo ? '#25D366' : undefined }}
                                onClick={() => c.mobileNo && window.open(`https://wa.me/91${c.mobileNo.replace(/\D/g, '')}`, '_blank')}>
                                <WhatsAppIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <SectionHeader title="🔥 Trending Products" subtitle="Views + cart conversion this period" />
              {procurement_radar.length === 0 ? (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
                  No product views tracked yet
                </Typography>
              ) : procurement_radar.slice(0, 8).map((p, i) => (
                <Box key={p.productId} display="flex" alignItems="center" gap={1.5} py={0.75}
                  sx={{ borderBottom: i < 7 ? '1px solid' : 'none', borderColor: 'divider' }}>
                  <Typography variant="caption" fontWeight={800} sx={{ width: 16, color: i === 0 ? 'error.main' : 'text.disabled' }}>
                    {i + 1}
                  </Typography>
                  <Box flex={1} minWidth={0}>
                    <Typography variant="body2" fontWeight={600} noWrap>{p.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{p.category || '—'}</Typography>
                  </Box>
                  <Box textAlign="right" flexShrink={0}>
                    <Typography variant="caption" color="text.secondary" display="block">{fmt(p.views)} views</Typography>
                    <Chip label={`${p.conversionRate}% →🛒`} size="small" sx={{
                      fontSize: '0.58rem', height: 16,
                      bgcolor: p.conversionRate >= 10 ? '#2e7d3220' : p.conversionRate >= 5 ? '#f57c0020' : '#94A3B820',
                      color: p.conversionRate >= 10 ? '#2e7d32' : p.conversionRate >= 5 ? '#f57c00' : '#94A3B8',
                    }} />
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

    </Box>
  );
};

export default CustomerAnalyticsDashboard;
